import type { ReactNode } from "react";
import React from "react";
import { AppConfigContext } from "../app/appConfig";
import { useAlert } from "../app/notificationToast";
import { RentalModel } from "../../api/rental/RentalModel";
import { RentalAdapter } from "../../api/rental/RentalAdapter";
import type {
  Rental,
  RentalAddItemRequest,
  RentalCreateRequest,
  RentalItem,
  RentalListFilters,
  RentalModelPricing,
  RentalModelPricingUpdate,
  RentalProduct,
  RentalProductRequest,
  RentalUpdateRequest,
} from "../../api/rental/Rental.typed";
import type { Equipment } from "../../api/equipment/Equipment.typed";

interface RentalContextType {
  listRentalProducts: () => Promise<RentalProduct[] | undefined>;
  createRentalProduct: (p: RentalProductRequest) => Promise<RentalProduct | undefined>;
  updateRentalProduct: (id: number, p: RentalProductRequest) => Promise<RentalProduct | undefined>;
  deleteRentalProduct: (id: number) => Promise<boolean | undefined>;
  listRentals: (filters?: RentalListFilters) => Promise<Rental[] | undefined>;
  getRental: (id: number) => Promise<Rental | undefined>;
  createRental: (p: RentalCreateRequest) => Promise<Rental | undefined>;
  updateRental: (id: number, p: RentalUpdateRequest) => Promise<Rental | undefined>;
  deleteRental: (id: number) => Promise<boolean | undefined>;
  addRentalItem: (rentalID: number, p: RentalAddItemRequest) => Promise<RentalItem | undefined>;
  returnRentalItem: (
    rentalID: number,
    itemID: number,
    state?: "returned" | "not_returned",
  ) => Promise<boolean | undefined>;
  removeRentalItem: (rentalID: number, itemID: number) => Promise<boolean | undefined>;
  listAvailableEquipments: (modelID?: number, query?: string) => Promise<Equipment[] | undefined>;
  listRentalModelPricing: () => Promise<RentalModelPricing[] | undefined>;
  updateRentalModelPricing: (
    modelID: number,
    p: RentalModelPricingUpdate,
  ) => Promise<boolean | undefined>;
}

const RentalContext = React.createContext<RentalContextType>(null!);

export function RentalProvider(props: { children: ReactNode }) {
  const appConfig = React.useContext(AppConfigContext);
  const { showAlert } = useAlert();

  const adapter = React.useMemo(() => {
    if (!appConfig) return null;
    const model = new RentalModel(appConfig);
    return new RentalAdapter(model);
  }, [appConfig]);

  const wrap = React.useCallback(
    async <T,>(fn: () => Promise<T>): Promise<T | undefined> => {
      try {
        return await fn();
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : "Erreur inconnue";
        showAlert(msg, "danger");
        return undefined;
      }
    },
    [showAlert],
  );

  const listRentalProducts = React.useCallback(async () => {
    if (!adapter) return undefined;
    return wrap(() => adapter.listRentalProducts());
  }, [adapter, wrap]);

  const createRentalProduct = React.useCallback(
    async (p: RentalProductRequest) => {
      if (!adapter) return undefined;
      const result = await wrap(() => adapter.createRentalProduct(p));
      if (result) showAlert("Kit créé", "success");
      return result;
    },
    [adapter, wrap, showAlert],
  );

  const updateRentalProduct = React.useCallback(
    async (id: number, p: RentalProductRequest) => {
      if (!adapter) return undefined;
      const result = await wrap(() => adapter.updateRentalProduct(id, p));
      if (result) showAlert("Kit mis à jour", "success");
      return result;
    },
    [adapter, wrap, showAlert],
  );

  const deleteRentalProduct = React.useCallback(
    async (id: number) => {
      if (!adapter) return undefined;
      const result = await wrap(async () => {
        await adapter.deleteRentalProduct(id);
        return true;
      });
      if (result) showAlert("Kit supprimé", "success");
      return result;
    },
    [adapter, wrap, showAlert],
  );

  const listRentals = React.useCallback(
    async (filters?: RentalListFilters) => {
      if (!adapter) return undefined;
      return wrap(() => adapter.listRentals(filters));
    },
    [adapter, wrap],
  );

  const getRental = React.useCallback(
    async (id: number) => {
      if (!adapter) return undefined;
      return wrap(() => adapter.getRental(id));
    },
    [adapter, wrap],
  );

  const createRental = React.useCallback(
    async (p: RentalCreateRequest) => {
      if (!adapter) return undefined;
      const result = await wrap(() => adapter.createRental(p));
      if (result) showAlert("Location créée", "success");
      return result;
    },
    [adapter, wrap, showAlert],
  );

  const updateRental = React.useCallback(
    async (id: number, p: RentalUpdateRequest) => {
      if (!adapter) return undefined;
      const result = await wrap(() => adapter.updateRental(id, p));
      if (result) showAlert("Location mise à jour", "success");
      return result;
    },
    [adapter, wrap, showAlert],
  );

  const deleteRental = React.useCallback(
    async (id: number) => {
      if (!adapter) return undefined;
      const result = await wrap(async () => {
        await adapter.deleteRental(id);
        return true;
      });
      if (result) showAlert("Location supprimée", "success");
      return result;
    },
    [adapter, wrap, showAlert],
  );

  const addRentalItem = React.useCallback(
    async (rentalID: number, p: RentalAddItemRequest) => {
      if (!adapter) return undefined;
      const result = await wrap(() => adapter.addRentalItem(rentalID, p));
      if (result) showAlert("Équipement ajouté au panier", "success");
      return result;
    },
    [adapter, wrap, showAlert],
  );

  const returnRentalItem = React.useCallback(
    async (rentalID: number, itemID: number, state: "returned" | "not_returned" = "returned") => {
      if (!adapter) return undefined;
      const result = await wrap(async () => {
        await adapter.returnRentalItem(rentalID, itemID, state);
        return true;
      });
      if (result) {
        showAlert(
          state === "not_returned" ? "Équipement marqué non rendu" : "Équipement rendu",
          state === "not_returned" ? "warning" : "success",
        );
      }
      return result;
    },
    [adapter, wrap, showAlert],
  );

  const removeRentalItem = React.useCallback(
    async (rentalID: number, itemID: number) => {
      if (!adapter) return undefined;
      const result = await wrap(async () => {
        await adapter.removeRentalItem(rentalID, itemID);
        return true;
      });
      if (result) showAlert("Équipement retiré", "success");
      return result;
    },
    [adapter, wrap, showAlert],
  );

  const listAvailableEquipments = React.useCallback(
    async (modelID?: number, query?: string) => {
      if (!adapter) return undefined;
      return wrap(() => adapter.listAvailableEquipments(modelID, query));
    },
    [adapter, wrap],
  );

  const listRentalModelPricing = React.useCallback(async () => {
    if (!adapter) return undefined;
    return wrap(() => adapter.listRentalModelPricing());
  }, [adapter, wrap]);

  const updateRentalModelPricing = React.useCallback(
    async (modelID: number, p: RentalModelPricingUpdate) => {
      if (!adapter) return undefined;
      const result = await wrap(async () => {
        await adapter.updateRentalModelPricing(modelID, p);
        return true;
      });
      if (result) showAlert("Tarif unitaire mis à jour", "success");
      return result;
    },
    [adapter, wrap, showAlert],
  );

  const value = React.useMemo<RentalContextType>(
    () => ({
      listRentalProducts,
      createRentalProduct,
      updateRentalProduct,
      deleteRentalProduct,
      listRentals,
      getRental,
      createRental,
      updateRental,
      deleteRental,
      addRentalItem,
      returnRentalItem,
      removeRentalItem,
      listAvailableEquipments,
      listRentalModelPricing,
      updateRentalModelPricing,
    }),
    [
      listRentalProducts,
      createRentalProduct,
      updateRentalProduct,
      deleteRentalProduct,
      listRentals,
      getRental,
      createRental,
      updateRental,
      deleteRental,
      addRentalItem,
      returnRentalItem,
      removeRentalItem,
      listAvailableEquipments,
      listRentalModelPricing,
      updateRentalModelPricing,
    ],
  );

  return <RentalContext.Provider value={value}>{props.children}</RentalContext.Provider>;
}

export function useRental(): RentalContextType {
  const ctx = React.useContext(RentalContext);
  if (!ctx) throw new Error("useRental must be used within RentalProvider");
  return ctx;
}
